/* ============================================================
   Unified Free Shipping + Discount Progress Meter
   Renders a single bar with discount checkpoints and a free
   shipping endpoint. Checkpoint data comes from data-attributes
   injected by free-shipping-component.liquid (settings-driven).
   ============================================================ */
class FreeShippingMeter extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.progressBar   = this.querySelector("[data-unified-progress]");
    this.progressMeter = this.querySelector("[data-unified-progress-meter]");
    this.messageEl     = this.querySelector("[data-unified-message]");
    this.shippingMessageEl = this.querySelector("[data-shipping-message]");
    this.shippingMarker = this.querySelector("[data-checkpoint-shipping]");

    // Build sorted checkpoints from DOM (discount markers only)
    this.checkpoints = Array.from(this.querySelectorAll("[data-checkpoint]"))
      .map(el => ({
        price : parseFloat(el.dataset.checkpointPrice) || 0,
        label : el.querySelector(".discount-marker__label")?.textContent.trim() || "",
        el,
      }))
      .filter(c => c.price > 0)
      .sort((a, b) => a.price - b.price);

    this.shippingPrice = parseFloat(this.shippingMarker?.dataset.checkpointPrice)
      || parseInt(window.free_shipping_price)
      || 0;

    // Use the server-computed bar_max so the fill % matches the Liquid-positioned markers exactly.
    // Falls back to the JS-computed max if the attribute is absent.
    const attrMax = parseFloat(this.dataset.barMax);
    this.maxPrice = attrMax > 0
      ? attrMax
      : Math.max(...this.checkpoints.map(c => c.price), this.shippingPrice, 1);

    this.addEventListener("change", this.initialize.bind(this));
    this.initialize();
  }

  initialize() {
    Shopify.getCart((cart) => {
      let totalPrice = cart.total_price;
      if ($("body").hasClass("setup_shipping_delivery")) {
        $(".cart-item[data-price-gift-card], .previewCartItem[data-price-gift-card]")
          .each(function () {
            totalPrice -= parseFloat($(this).attr("data-price-gift-card")) || 0;
          });
      }
      this.render(totalPrice / 100);
    });
  }

  render(total) {
    const percent = Math.min((total / this.maxPrice) * 100, 100);

    // Update marker reached states
    this.checkpoints.forEach(cp => {
      cp.el.classList.toggle("discount-marker--reached", total >= cp.price);
    });
    if (this.shippingMarker) {
      this.shippingMarker.classList.toggle("discount-marker--reached", total >= this.shippingPrice);
    }

    // Show/hide the bar
    if (total <= 0) {
      this.progressBar?.classList.add("progress-hidden");
    } else {
      this.progressBar?.classList.remove("progress-hidden");
    }

    setTimeout(() => {
      if (this.progressMeter) this.progressMeter.style.width = `${percent}%`;
      if (this.messageEl)     this.messageEl.innerHTML = this.buildMessage(total);
      if (this.shippingMessageEl) {
        this.shippingMessageEl.innerHTML = this.buildShippingMessage(total);
        this.shippingMessageEl.classList.toggle(
          "message--shipping-reached",
          this.shippingPrice > 0 && total >= this.shippingPrice
        );
      }
      this.updateProgressClass(percent, total);

      if ($(".dropdown-item[data-currency]").length) {
        if (
          (window.show_multiple_currencies &&
            typeof Currency != "undefined" &&
            Currency.currentCurrency != shopCurrency) ||
          window.show_auto_currency
        ) {
          Currency.convertAll(
            window.shop_currency,
            $("#currencies .active").attr("data-currency"),
            "span.money",
            "money_format"
          );
        }
      }
    }, 400);
  }

  // Free shipping line — tracks only the shipping threshold
  buildShippingMessage(total) {
    if (!this.shippingPrice || this.shippingPrice <= 0) return "";

    if (total >= this.shippingPrice) {
      return "Você ganhou <strong>frete grátis</strong>!";
    }

    const remaining = Shopify.formatMoney((this.shippingPrice - Math.max(total, 0)) * 100, window.money_format);
    return `Faltam apenas <strong>${remaining}</strong> para ganhar <strong>frete grátis</strong>!`;
  }

  // Discount line — tracks only the discount checkpoints
  buildMessage(total) {
    if (!this.checkpoints.length) return "";

    if (total <= 0) {
      const first = this.checkpoints[0];
      return `Adicione <strong>${Shopify.formatMoney(first.price * 100, window.money_format)}</strong> para ganhar <strong>${first.label}</strong>!`;
    }

    let lastReached = null;
    let next = null;
    for (const m of this.checkpoints) {
      if (total >= m.price) lastReached = m;
      else { next = m; break; }
    }

    if (!next) {
      return `Parabéns! Você desbloqueou <strong>${lastReached.label}</strong> na sua compra! 🎉`;
    }

    const remaining = Shopify.formatMoney((next.price - total) * 100, window.money_format);

    if (lastReached) {
      return `<strong>${lastReached.label}</strong> desbloqueado! Faltam <strong>${remaining}</strong> para ganhar <strong>${next.label}</strong>!`;
    }

    return `Faltam <strong>${remaining}</strong> para ganhar <strong>${next.label}</strong>!`;
  }

  updateProgressClass(percent, total) {
    if (!this.progressBar) return;
    this.progressBar.classList.remove("progress-30", "progress-60", "progress-100", "progress-free");
    if (total >= this.shippingPrice) {
      this.progressBar.classList.add("progress-free");
    } else if (percent >= 60) {
      this.progressBar.classList.add("progress-100");
    } else if (percent >= 30) {
      this.progressBar.classList.add("progress-60");
    } else if (percent > 0) {
      this.progressBar.classList.add("progress-30");
    }
  }
}

window.addEventListener("load", () => {
  customElements.define("free-shipping-component", FreeShippingMeter);
});

/* ============================================================
   Discount Progress Meter — standalone custom element
   Triggered by dispatchChangeForShippingMessage (data-free-shipping-wrapper)
   ============================================================ */
class DiscountProgressMeter extends HTMLElement {
  constructor() {
    super();
  }

  static checkpoints = [
    { price: 199, label: "R$20 OFF" },
    { price: 399, label: "R$50 OFF" },
    { price: 599, label: "R$100 OFF" },
  ];
  static maxPrice = 599;

  connectedCallback() {
    this.progressBar = this.querySelector("[data-discount-progress]");
    this.discountMeter = this.querySelector("[data-discount-progress-meter]");
    this.discountMessage = this.querySelector("[data-discount-message]");
    this.markers = this.querySelectorAll(".discount-marker");

    // Triggered by halo.dispatchChangeForShippingMessage() after every cart update
    this.addEventListener("change", this.onCartChange.bind(this));

    this.initialize();
  }

  initialize() {
    Shopify.getCart((cart) => {
      const total = parseInt(cart.total_price) / 100;
      this.render(total);
    });
  }

  onCartChange() {
    this.initialize();
  }

  render(total) {
    const { checkpoints, maxPrice } = DiscountProgressMeter;
    const percent = Math.min((total / maxPrice) * 100, 100);

    this.markers.forEach((marker, i) => {
      marker.classList.toggle("discount-marker--reached", total >= checkpoints[i].price);
    });

    let text;
    if (total <= 0) {
      if (this.progressBar) this.progressBar.classList.add("progress-hidden");
      text =
        "Compre <strong>" +
        Shopify.formatMoney(checkpoints[0].price * 100, window.money_format) +
        "</strong> e ganhe <strong>" + checkpoints[0].label + "</strong>!";
    } else if (total < checkpoints[0].price) {
      if (this.progressBar) this.progressBar.classList.remove("progress-hidden");
      const rem = checkpoints[0].price - total;
      text =
        "Faltam <strong>" +
        Shopify.formatMoney(rem * 100, window.money_format) +
        "</strong> para ganhar <strong>" + checkpoints[0].label + "</strong>!";
    } else if (total < checkpoints[1].price) {
      if (this.progressBar) this.progressBar.classList.remove("progress-hidden");
      const rem = checkpoints[1].price - total;
      text =
        "<strong>" + checkpoints[0].label + "</strong> desbloqueado! Faltam <strong>" +
        Shopify.formatMoney(rem * 100, window.money_format) +
        "</strong> para ganhar <strong>" + checkpoints[1].label + "</strong>!";
    } else if (total < checkpoints[2].price) {
      if (this.progressBar) this.progressBar.classList.remove("progress-hidden");
      const rem = checkpoints[2].price - total;
      text =
        "<strong>" + checkpoints[1].label + "</strong> desbloqueado! Faltam <strong>" +
        Shopify.formatMoney(rem * 100, window.money_format) +
        "</strong> para ganhar <strong>" + checkpoints[2].label + "</strong>!";
    } else {
      if (this.progressBar) this.progressBar.classList.remove("progress-hidden");
      text =
        "Parabéns! Você desbloqueou <strong>" + checkpoints[2].label + "</strong> na sua compra! 🎉";
    }

    setTimeout(() => {
      if (this.discountMeter) this.discountMeter.style.width = `${percent}%`;
      if (this.discountMessage) this.discountMessage.innerHTML = text;

      if ($(".dropdown-item[data-currency]").length) {
        if (
          (window.show_multiple_currencies &&
            typeof Currency != "undefined" &&
            Currency.currentCurrency != shopCurrency) ||
          window.show_auto_currency
        ) {
          Currency.convertAll(
            window.shop_currency,
            $("#currencies .active").attr("data-currency"),
            "span.money",
            "money_format"
          );
        }
      }
    }, 400);
  }
}

window.addEventListener("load", () => {
  customElements.define("discount-progress-component", DiscountProgressMeter);
});

/* ============================================================
   Quantity Discount Steps — steps UI driven by cart item count.
   Checkpoints come from data-attributes set by
   quantity-discount-component.liquid (settings-driven).
   Participates in dispatchChangeForShippingMessage via
   data-free-shipping-wrapper.
   ============================================================ */
class QuantityDiscountMeter extends HTMLElement {
  connectedCallback() {
    this.messageEl = this.querySelector("[data-qty-message]");

    this.checkpoints = Array.from(this.querySelectorAll("[data-qty-step]"))
      .map(el => ({
        qty  : parseInt(el.dataset.checkpointQty) || 0,
        label: el.querySelector(".qty-step__label")?.textContent.trim() || "",
        el,
      }))
      .filter(c => c.qty > 0)
      .sort((a, b) => a.qty - b.qty);

    this.addEventListener("change", this.initialize.bind(this));
    this.initialize();
  }

  initialize() {
    Shopify.getCart((cart) => {
      this.render(cart.item_count);
    });
  }

  render(itemCount) {
    this.checkpoints.forEach(cp => {
      cp.el.classList.toggle("qty-step--reached", itemCount >= cp.qty);
    });

    setTimeout(() => {
      if (this.messageEl) this.messageEl.innerHTML = this.buildMessage(itemCount);
    }, 400);
  }

  buildMessage(itemCount) {
    if (!this.checkpoints.length) return "";

    if (itemCount <= 0) {
      const first = this.checkpoints[0];
      const unit  = first.qty === 1 ? "peça" : "peças";
      return `Adicione <strong>${first.qty} ${unit}</strong> para ganhar <strong>${first.label}</strong>!`;
    }

    let lastReached = null;
    let next = null;
    for (const cp of this.checkpoints) {
      if (itemCount >= cp.qty) lastReached = cp;
      else { next = cp; break; }
    }

    if (!next) {
      return `Parabéns! Você ganhou <strong>${lastReached.label}</strong>! 🎉`;
    }

    const diff = next.qty - itemCount;
    const unit = diff === 1 ? "peça" : "peças";

    if (lastReached) {
      return `<strong>${lastReached.label}</strong> desbloqueado! Adicione <strong>${diff} ${unit}</strong> para ganhar <strong>${next.label}</strong>!`;
    }

    return `Adicione <strong>${diff} ${unit}</strong> para ganhar <strong>${next.label}</strong>!`;
  }
}

window.addEventListener("load", () => {
  customElements.define("quantity-discount-component", QuantityDiscountMeter);
});
